import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreVertical, Pencil, Trash2, Package, Loader2, Upload, X, Image as ImageIcon, Wine, Tag, Percent, EyeOff, ScanBarcode } from 'lucide-react';
import { toast } from 'sonner';
import useBarcodeScanner from '../hooks/useBarcodeScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Cerveja', 'Refrigerante', 'Mercearia', 'Destilados', 'Vinhos', 'Combo', 'Gelo', 'Tabacaria e Cigarro', 'Energéticos'];
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [scannerEnabled, setScannerEnabled] = useState(true);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'Cerveja',
        price: '',
        cost_price: '',
        stock_quantity: '',
        min_stock: '5',
        barcode: '',
        image_url: '',
        // Campos para venda por dose
        sold_by_dose: false,
        dose_price: '',
        doses_per_bottle: '',
        open_bottle_doses_remaining: '0',
        // Campos de promoção
        on_promotion: false,
        promotional_price: '',
        promotion_end_date: '',
        // Ocultar dose da vitrine
        hide_dose_from_vitrine: false
    });

    // Handler para leitura de código de barras
    const handleBarcodeScan = useCallback(async (barcode) => {
        console.log('[PRODUTOS] Scanner detectou código:', barcode);
        console.log('[PRODUTOS] Caracteres do código:', barcode.split('').map(c => `${c}(${c.charCodeAt(0)})`).join(' '));
        
        // Limpar código - remover caracteres não numéricos se o código parecer ser EAN/UPC
        let cleanBarcode = barcode;
        
        // Se o código tem mais de 8 dígitos e começa com letra, pode ser erro do scanner
        if (barcode.length >= 8 && /^[A-Za-z]/.test(barcode)) {
            const numbersOnly = barcode.replace(/[^0-9]/g, '');
            if (numbersOnly.length >= 8) {
                console.log('[PRODUTOS] Código parece ter prefixo inválido, usando apenas números:', numbersOnly);
                cleanBarcode = numbersOnly;
            }
        }
        
        // Desabilitar scanner temporariamente
        setScannerEnabled(false);
        
        try {
            // Buscar produto pelo código de barras
            console.log('[PRODUTOS] Buscando produto no backend com código:', cleanBarcode);
            const response = await axios.get(`${API_URL}/api/products/barcode/${cleanBarcode}`);
            const product = response.data;
            console.log('[PRODUTOS] Produto encontrado:', product.name);
            
            // Produto encontrado - abrir para edição
            toast.success(`Produto encontrado: ${product.name}`, {
                icon: '📦',
                duration: 3000
            });
            
            // Abrir dialog de edição
            setEditingProduct(product);
            setFormData({
                name: product.name,
                description: product.description || '',
                category: product.category,
                price: product.price.toString(),
                cost_price: product.cost_price.toString(),
                stock_quantity: product.stock_quantity.toString(),
                min_stock: product.min_stock.toString(),
                barcode: product.barcode || '',
                image_url: product.image_url || '',
                sold_by_dose: product.sold_by_dose || false,
                dose_price: product.dose_price ? product.dose_price.toString() : '',
                doses_per_bottle: product.doses_per_bottle ? product.doses_per_bottle.toString() : '',
                open_bottle_doses_remaining: product.open_bottle_doses_remaining ? product.open_bottle_doses_remaining.toString() : '0',
                on_promotion: product.on_promotion || false,
                promotional_price: product.promotional_price ? product.promotional_price.toString() : '',
                promotion_end_date: product.promotion_end_date ? product.promotion_end_date.split('T')[0] : '',
                hide_dose_from_vitrine: product.hide_dose_from_vitrine || false
            });
            setImagePreview(product.image_url || null);
            setDialogOpen(true);
            
        } catch (error) {
            console.error('[PRODUTOS] Erro ao buscar produto:', error);
            if (error.response?.status === 404) {
                // Produto não encontrado - abrir formulário de cadastro com barcode preenchido
                toast.info(`Novo produto! Código: ${cleanBarcode}`, {
                    icon: '🆕',
                    duration: 3000
                });
                
                setEditingProduct(null);
                setFormData({
                    name: '',
                    description: '',
                    category: 'Mercearia',
                    price: '',
                    cost_price: '',
                    stock_quantity: '',
                    min_stock: '5',
                    barcode: cleanBarcode, // Preencher código de barras limpo
                    image_url: '',
                    sold_by_dose: false,
                    dose_price: '',
                    doses_per_bottle: '',
                    open_bottle_doses_remaining: '0',
                    on_promotion: false,
                    promotional_price: '',
                    promotion_end_date: '',
                    hide_dose_from_vitrine: false
                });
                setImagePreview(null);
                setDialogOpen(true);
            } else {
                toast.error('Erro ao buscar produto');
            }
        } finally {
            // Reativar scanner após um delay
            setTimeout(() => setScannerEnabled(true), 1000);
        }
    }, []);

    // Hook do scanner - ativo apenas quando dialog está fechado
    const { isScanning } = useBarcodeScanner({
        enabled: scannerEnabled && !dialogOpen,
        onScan: handleBarcodeScan,
        minLength: 4,
        maxDelay: 150  // Aumentado para compatibilidade com scanners mais lentos
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/products?active_only=false`);
            setProducts(response.data);
        } catch (error) {
            toast.error('Erro ao carregar produtos');
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const openCreateDialog = () => {
        setEditingProduct(null);
        setFormData({
            name: '',
            description: '',
            category: 'Cerveja',
            price: '',
            cost_price: '',
            stock_quantity: '',
            min_stock: '5',
            barcode: '',
            image_url: '',
            // Campos para venda por dose
            sold_by_dose: false,
            dose_price: '',
            doses_per_bottle: '',
            open_bottle_doses_remaining: '0',
            // Campos de promoção
            on_promotion: false,
            promotional_price: '',
            promotion_end_date: '',
            // Ocultar dose da vitrine
            hide_dose_from_vitrine: false
        });
        setImagePreview(null);
        setDialogOpen(true);
    };

    const openEditDialog = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            description: product.description || '',
            category: product.category,
            price: product.price.toString(),
            cost_price: product.cost_price.toString(),
            stock_quantity: product.stock_quantity.toString(),
            min_stock: product.min_stock.toString(),
            barcode: product.barcode || '',
            image_url: product.image_url || '',
            // Campos para venda por dose
            sold_by_dose: product.sold_by_dose || false,
            dose_price: product.dose_price ? product.dose_price.toString() : '',
            doses_per_bottle: product.doses_per_bottle ? product.doses_per_bottle.toString() : '',
            open_bottle_doses_remaining: product.open_bottle_doses_remaining ? product.open_bottle_doses_remaining.toString() : '0',
            // Campos de promoção
            on_promotion: product.on_promotion || false,
            promotional_price: product.promotional_price ? product.promotional_price.toString() : '',
            promotion_end_date: product.promotion_end_date ? product.promotion_end_date.split('T')[0] : '',
            // Ocultar dose da vitrine
            hide_dose_from_vitrine: product.hide_dose_from_vitrine || false
        });
        setImagePreview(product.image_url || null);
        setDialogOpen(true);
    };

    const uploadToCloudinary = async (file) => {
        try {
            // Get signature from backend
            const signatureResponse = await axios.get(`${API_URL}/api/cloudinary/signature?folder=products`);
            const { signature, timestamp, cloud_name, api_key, folder } = signatureResponse.data;

            // Upload to Cloudinary
            const formData = new FormData();
            formData.append('file', file);
            formData.append('api_key', api_key);
            formData.append('timestamp', timestamp);
            formData.append('signature', signature);
            formData.append('folder', folder);

            const uploadResponse = await fetch(
                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                { method: 'POST', body: formData }
            );

            const result = await uploadResponse.json();
            
            if (result.error) {
                throw new Error(result.error.message);
            }

            return result.secure_url;
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            throw error;
        }
    };

    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            toast.error('Formato não suportado. Use JPG, PNG ou WebP');
            return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
            toast.error('Imagem muito grande. Máximo 2MB');
            return;
        }

        // Show preview immediately
        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

        // Upload to Cloudinary
        setUploading(true);
        try {
            const imageUrl = await uploadToCloudinary(file);
            setFormData(prev => ({ ...prev, image_url: imageUrl }));
            toast.success('Imagem enviada com sucesso!');
        } catch (error) {
            toast.error('Erro ao enviar imagem');
            setImagePreview(formData.image_url || null);
        } finally {
            setUploading(false);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({ ...prev, image_url: '' }));
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.price || !formData.cost_price) {
            toast.error('Preencha os campos obrigatórios');
            return;
        }

        // Validação para produtos vendidos por dose
        if (formData.sold_by_dose) {
            if (!formData.dose_price || parseFloat(formData.dose_price) <= 0) {
                toast.error('Informe o preço da dose');
                return;
            }
            if (!formData.doses_per_bottle || parseInt(formData.doses_per_bottle) <= 0) {
                toast.error('Informe quantas doses a garrafa rende');
                return;
            }
        }

        // Validação para promoção
        if (formData.on_promotion) {
            if (!formData.promotional_price || parseFloat(formData.promotional_price) <= 0) {
                toast.error('Informe o preço promocional');
                return;
            }
            if (parseFloat(formData.promotional_price) >= parseFloat(formData.price)) {
                toast.error('Preço promocional deve ser menor que o preço normal');
                return;
            }
        }

        setSaving(true);

        try {
            const data = {
                ...formData,
                price: parseFloat(formData.price),
                cost_price: parseFloat(formData.cost_price),
                stock_quantity: parseInt(formData.stock_quantity) || 0,
                min_stock: parseInt(formData.min_stock) || 5,
                // Campos para venda por dose
                sold_by_dose: formData.sold_by_dose,
                dose_price: formData.sold_by_dose ? parseFloat(formData.dose_price) || 0 : null,
                doses_per_bottle: formData.sold_by_dose ? parseInt(formData.doses_per_bottle) || 0 : null,
                open_bottle_doses_remaining: formData.sold_by_dose ? parseInt(formData.open_bottle_doses_remaining) || 0 : 0,
                // Campos de promoção
                on_promotion: formData.on_promotion,
                promotional_price: formData.on_promotion ? parseFloat(formData.promotional_price) : null,
                promotion_end_date: formData.on_promotion && formData.promotion_end_date ? `${formData.promotion_end_date}T23:59:59Z` : null,
                // Ocultar dose da vitrine
                hide_dose_from_vitrine: formData.hide_dose_from_vitrine
            };

            if (editingProduct) {
                await axios.put(`${API_URL}/api/products/${editingProduct.id}`, data);
                toast.success('Produto atualizado!');
            } else {
                await axios.post(`${API_URL}/api/products`, data);
                toast.success('Produto criado!');
            }

            setDialogOpen(false);
            fetchProducts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (product) => {
        if (!window.confirm(`Excluir "${product.name}"?`)) return;

        try {
            await axios.delete(`${API_URL}/api/products/${product.id}`);
            toast.success('Produto excluído!');
            fetchProducts();
        } catch (error) {
            toast.error('Erro ao excluir produto');
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4 lg:space-y-6 animate-fade-in-up" data-testid="products-page">
            {/* Scanner Indicator */}
            {isScanning && (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-pulse">
                    <ScanBarcode className="w-5 h-5" />
                    <span className="font-medium">Lendo código de barras...</span>
                </div>
            )}
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Produtos</h1>
                    <p className="text-gray-400 text-sm lg:text-base mt-1">
                        Gerencie seu catálogo de produtos
                        <span className="ml-2 inline-flex items-center gap-1 text-red-400">
                            <ScanBarcode className="w-4 h-4" />
                            Scanner ativo
                        </span>
                    </p>
                </div>
                <Button
                    className="bg-red-500 text-white hover:bg-red-400 font-bold w-full sm:w-auto"
                    onClick={openCreateDialog}
                    data-testid="add-product-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Novo Produto
                </Button>
            </div>

            <Card className="bg-[#121212] border-[#262626]">
                <CardHeader className="p-3 lg:p-6">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-gray-500" />
                            <Input
                                placeholder="Buscar produtos..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 lg:pl-11 bg-[#0a0a0a] border-[#262626] text-white h-10"
                                data-testid="search-products-input"
                            />
                        </div>
                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="w-full sm:w-40 lg:w-48 bg-[#0a0a0a] border-[#262626] text-white h-10" data-testid="filter-category">
                                <SelectValue placeholder="Categoria" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#262626]">
                                <SelectItem value="all">Todas</SelectItem>
                                {CATEGORIES.map(cat => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>
                <CardContent className="p-0 lg:p-6 lg:pt-0">
                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-2 p-3">
                        {filteredProducts.map(product => {
                            const profitMargin = product.profit_margin || (product.price - product.cost_price);
                            const profitPct = product.profit_percentage || (profitMargin / product.cost_price * 100);
                            const isLowStock = product.stock_quantity <= product.min_stock;

                            return (
                                <div 
                                    key={product.id}
                                    className="p-3 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]"
                                    data-testid={`product-row-${product.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <div className="w-10 h-10 bg-[#1a1a1a] rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                {product.image_url ? (
                                                    <img 
                                                        src={product.image_url} 
                                                        alt={product.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <Package className="w-5 h-5 text-rr-yellow-500/50" />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-white font-medium text-sm truncate">{product.name}</p>
                                                <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                    <Badge variant="outline" className="border-[#333] text-gray-400 text-[10px]">
                                                        {product.category}
                                                    </Badge>
                                                    {product.on_promotion && (
                                                        <Badge className="bg-red-500 text-white text-[10px] px-1">
                                                            PROMO
                                                        </Badge>
                                                    )}
                                                    {product.sold_by_dose && product.hide_dose_from_vitrine && (
                                                        <Badge className="bg-purple-600/50 text-purple-300 text-[10px] px-1">
                                                            <EyeOff className="w-2.5 h-2.5 mr-0.5 inline" />
                                                            Dose Oculta
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white h-8 w-8">
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-[#121212] border-[#262626]">
                                                <DropdownMenuItem 
                                                    className="text-gray-300 focus:bg-white/10 focus:text-white cursor-pointer"
                                                    onClick={() => openEditDialog(product)}
                                                >
                                                    <Pencil className="w-4 h-4 mr-2" />
                                                    Editar
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                    className="text-red-500 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                                                    onClick={() => handleDelete(product)}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Excluir
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between text-xs">
                                        <div className="flex items-center gap-3">
                                            {product.on_promotion ? (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-gray-500 line-through text-[10px]">{formatCurrency(product.price)}</span>
                                                    <span className="text-red-500 font-bold">{formatCurrency(product.promotional_price)}</span>
                                                </div>
                                            ) : (
                                                <span className="text-rr-yellow-500 font-bold">{formatCurrency(product.price)}</span>
                                            )}
                                            {product.sold_by_dose ? (
                                                <span className="text-purple-400 flex items-center gap-1">
                                                    <Wine className="w-3 h-3" />
                                                    {product.stock_quantity} gf / {product.open_bottle_doses_remaining} ds
                                                </span>
                                            ) : (
                                                <span className={`${isLowStock ? 'text-red-400' : 'text-gray-500'}`}>
                                                    Est: {product.stock_quantity}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-green-400">+{profitPct.toFixed(0)}%</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden lg:block">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-[#262626] hover:bg-transparent">
                                <TableHead className="text-gray-400">Produto</TableHead>
                                <TableHead className="text-gray-400">Categoria</TableHead>
                                <TableHead className="text-gray-400 text-right">Preço</TableHead>
                                <TableHead className="text-gray-400 text-right">Custo</TableHead>
                                <TableHead className="text-gray-400 text-right">Lucro</TableHead>
                                <TableHead className="text-gray-400 text-center">Estoque</TableHead>
                                <TableHead className="text-gray-400 w-10"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProducts.map(product => {
                                const profitMargin = product.profit_margin || (product.price - product.cost_price);
                                const profitPct = product.profit_percentage || (profitMargin / product.cost_price * 100);
                                const isLowStock = product.stock_quantity <= product.min_stock;

                                return (
                                    <TableRow 
                                        key={product.id} 
                                        className="border-[#262626] hover:bg-white/5"
                                        data-testid={`product-row-${product.id}`}
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-[#1a1a1a] rounded-md flex items-center justify-center overflow-hidden">
                                                    {product.image_url ? (
                                                        <img 
                                                            src={product.image_url} 
                                                            alt={product.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <Package className="w-5 h-5 text-rr-yellow-500/50" />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className="text-white font-medium">{product.name}</p>
                                                    {product.barcode && (
                                                        <p className="text-gray-500 text-xs">{product.barcode}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 flex-wrap">
                                                <Badge variant="outline" className="border-[#333] text-gray-400">
                                                    {product.category}
                                                </Badge>
                                                {product.sold_by_dose && product.hide_dose_from_vitrine && (
                                                    <Badge className="bg-purple-600/50 text-purple-300 text-[10px]">
                                                        <EyeOff className="w-3 h-3 mr-0.5 inline" />
                                                        Dose Oculta
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right text-rr-yellow-500 font-medium">
                                            {formatCurrency(product.price)}
                                        </TableCell>
                                        <TableCell className="text-right text-gray-400">
                                            {formatCurrency(product.cost_price)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <span className="text-green-400">
                                                {formatCurrency(profitMargin)}
                                            </span>
                                            <span className="text-gray-500 text-xs ml-1">
                                                ({profitPct.toFixed(0)}%)
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {product.sold_by_dose ? (
                                                <div className="flex items-center justify-center gap-1">
                                                    <Wine className="w-4 h-4 text-purple-400" />
                                                    <span className="text-purple-400 text-xs">
                                                        {product.stock_quantity}gf/{product.open_bottle_doses_remaining}ds
                                                    </span>
                                                </div>
                                            ) : (
                                                <Badge 
                                                    variant={isLowStock ? "destructive" : "secondary"}
                                                    className={isLowStock ? "bg-red-500/20 text-red-400" : "bg-[#1a1a1a] text-gray-300"}
                                                >
                                                    {product.stock_quantity}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                                                        <MoreVertical className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="bg-[#121212] border-[#262626]">
                                                    <DropdownMenuItem 
                                                        className="text-gray-300 focus:bg-white/10 focus:text-white cursor-pointer"
                                                        onClick={() => openEditDialog(product)}
                                                    >
                                                        <Pencil className="w-4 h-4 mr-2" />
                                                        Editar
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem 
                                                        className="text-red-500 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                                                        onClick={() => handleDelete(product)}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Excluir
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </div>
                    {filteredProducts.length === 0 && (
                        <div className="text-center py-8 lg:py-12 text-gray-500 text-sm lg:text-base">
                            Nenhum produto encontrado
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Product Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white w-[calc(100%-2rem)] sm:w-full max-w-lg mx-auto max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg lg:text-xl font-heading">
                            {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3 lg:space-y-4 py-2 lg:py-4">
                        {/* Image Upload Section */}
                        <div className="space-y-2">
                            <Label className="text-gray-400">Imagem do Produto</Label>
                            <div className="flex items-start gap-4">
                                {/* Image Preview */}
                                <div className="relative w-24 h-24 bg-[#1a1a1a] rounded-lg border-2 border-dashed border-[#333] flex items-center justify-center overflow-hidden">
                                    {uploading ? (
                                        <Loader2 className="w-6 h-6 text-rr-yellow-500 animate-spin" />
                                    ) : imagePreview ? (
                                        <>
                                            <img 
                                                src={imagePreview} 
                                                alt="Preview" 
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={removeImage}
                                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                            >
                                                <X className="w-3 h-3 text-white" />
                                            </button>
                                        </>
                                    ) : (
                                        <ImageIcon className="w-8 h-8 text-gray-600" />
                                    )}
                                </div>
                                
                                {/* Upload Button */}
                                <div className="flex-1 space-y-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={handleImageSelect}
                                        className="hidden"
                                        id="image-upload"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full border-[#333] text-gray-300 hover:bg-[#1a1a1a]"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        {uploading ? 'Enviando...' : 'Escolher Imagem'}
                                    </Button>
                                    <p className="text-xs text-gray-500">
                                        JPG, PNG ou WebP. Máx 2MB
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Nome *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Nome do produto"
                                data-testid="product-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Categoria *</Label>
                            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                                <SelectTrigger className="bg-[#121212] border-[#262626] text-white h-10" data-testid="product-category-select">
                                    <SelectValue placeholder="Selecione uma categoria" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#262626] text-white z-[9999]">
                                    {CATEGORIES.map(cat => (
                                        <SelectItem key={cat} value={cat} className="text-white hover:bg-rr-yellow-500/20 focus:bg-rr-yellow-500/20 cursor-pointer">{cat}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400">Preço Venda *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="0.00"
                                    data-testid="product-price-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400">Preço Custo *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.cost_price}
                                    onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="0.00"
                                    data-testid="product-cost-input"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-gray-400">Estoque Inicial</Label>
                                <Input
                                    type="number"
                                    value={formData.stock_quantity}
                                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="0"
                                    data-testid="product-stock-input"
                                    disabled={formData.sold_by_dose}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-gray-400">Estoque Mínimo</Label>
                                <Input
                                    type="number"
                                    value={formData.min_stock}
                                    onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                                    className="bg-[#121212] border-[#262626] text-white"
                                    placeholder="5"
                                    data-testid="product-min-stock-input"
                                />
                            </div>
                        </div>

                        {/* Seção Venda por Dose - Adega */}
                        <div className="border border-[#262626] rounded-lg p-4 space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Wine className="w-5 h-5 text-purple-400" />
                                    <Label className="text-gray-200 font-medium">Produto vendido por dose?</Label>
                                </div>
                                <Switch
                                    checked={formData.sold_by_dose}
                                    onCheckedChange={(checked) => setFormData({ ...formData, sold_by_dose: checked })}
                                    data-testid="sold-by-dose-switch"
                                />
                            </div>

                            {formData.sold_by_dose && (
                                <div className="space-y-4 animate-fade-in-up">
                                    <p className="text-sm text-gray-500">
                                        Configure o controle de estoque para bebidas fracionadas (vinhos, whisky, etc.)
                                    </p>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-gray-400">Preço da Dose *</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={formData.dose_price}
                                                onChange={(e) => setFormData({ ...formData, dose_price: e.target.value })}
                                                className="bg-[#121212] border-[#262626] text-white"
                                                placeholder="0.00"
                                                data-testid="dose-price-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-gray-400">Doses por Garrafa *</Label>
                                            <Input
                                                type="number"
                                                value={formData.doses_per_bottle}
                                                onChange={(e) => setFormData({ ...formData, doses_per_bottle: e.target.value })}
                                                className="bg-[#121212] border-[#262626] text-white"
                                                placeholder="Ex: 5"
                                                data-testid="doses-per-bottle-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-gray-400">Doses na Garrafa Aberta (se houver)</Label>
                                        <Input
                                            type="number"
                                            value={formData.open_bottle_doses_remaining}
                                            onChange={(e) => setFormData({ ...formData, open_bottle_doses_remaining: e.target.value })}
                                            className="bg-[#121212] border-[#262626] text-white"
                                            placeholder="0"
                                            data-testid="doses-remaining-input"
                                        />
                                        <p className="text-xs text-gray-500">Se já existe garrafa aberta, informe quantas doses restam</p>
                                    </div>

                                    <div className="bg-purple-900/20 border border-purple-500/30 rounded p-3 text-sm">
                                        <p className="text-purple-300">
                                            <strong>Estoque Total:</strong> {' '}
                                            {(parseInt(formData.stock_quantity) || 0) * (parseInt(formData.doses_per_bottle) || 1) + (parseInt(formData.open_bottle_doses_remaining) || 0)} doses disponíveis
                                        </p>
                                        <p className="text-gray-400 text-xs mt-1">
                                            ({formData.stock_quantity || 0} garrafas × {formData.doses_per_bottle || 0} doses + {formData.open_bottle_doses_remaining || 0} doses na aberta)
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Seção de Promoção */}
                        <div className="space-y-4 border-t border-[#262626] pt-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-red-500" />
                                    <Label className="text-white font-medium">Promoção</Label>
                                </div>
                                <Switch
                                    checked={formData.on_promotion}
                                    onCheckedChange={(checked) => setFormData({ ...formData, on_promotion: checked })}
                                    data-testid="promotion-switch"
                                />
                            </div>

                            {formData.on_promotion && (
                                <div className="space-y-4 pl-6 border-l-2 border-red-500/30">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-gray-400">Preço Promocional *</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={formData.promotional_price}
                                                onChange={(e) => setFormData({ ...formData, promotional_price: e.target.value })}
                                                className="bg-[#121212] border-[#262626] text-white"
                                                placeholder="0.00"
                                                data-testid="promotional-price-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-gray-400">Válido até</Label>
                                            <Input
                                                type="date"
                                                value={formData.promotion_end_date}
                                                onChange={(e) => setFormData({ ...formData, promotion_end_date: e.target.value })}
                                                className="bg-[#121212] border-[#262626] text-white"
                                                data-testid="promotion-end-date-input"
                                            />
                                        </div>
                                    </div>

                                    {formData.promotional_price && formData.price && (
                                        <div className="bg-red-900/20 border border-red-500/30 rounded p-3 text-sm">
                                            <div className="flex items-center gap-2 text-red-300">
                                                <Percent className="w-4 h-4" />
                                                <strong>
                                                    {Math.round(((parseFloat(formData.price) - parseFloat(formData.promotional_price)) / parseFloat(formData.price)) * 100)}% de desconto
                                                </strong>
                                            </div>
                                            <p className="text-gray-400 text-xs mt-1">
                                                De {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(formData.price))} por {' '}
                                                <span className="text-red-400 font-bold">
                                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(formData.promotional_price))}
                                                </span>
                                            </p>
                                            {parseFloat(formData.promotional_price) > parseFloat(formData.cost_price) && (
                                                <p className="text-green-400 text-xs mt-1">
                                                    Lucro: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(formData.promotional_price) - parseFloat(formData.cost_price))}
                                                </p>
                                            )}
                                            {parseFloat(formData.promotional_price) <= parseFloat(formData.cost_price) && (
                                                <p className="text-yellow-400 text-xs mt-1">
                                                    ⚠️ Atenção: Preço promocional igual ou menor que o custo!
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Seção Ocultar Dose da Vitrine - Só aparece se sold_by_dose estiver ativo */}
                        {formData.sold_by_dose && (
                            <div className="space-y-2 border-t border-[#262626] pt-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <EyeOff className="w-4 h-4 text-purple-500" />
                                        <div>
                                            <Label className="text-white font-medium">Ocultar Dose da Vitrine</Label>
                                            <p className="text-xs text-gray-500">Vende garrafa na vitrine, mas não a dose</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={formData.hide_dose_from_vitrine}
                                        onCheckedChange={(checked) => setFormData({ ...formData, hide_dose_from_vitrine: checked })}
                                        data-testid="hide-dose-from-vitrine-switch"
                                    />
                                </div>
                                {formData.hide_dose_from_vitrine && (
                                    <div className="bg-purple-900/30 border border-purple-500/30 rounded p-3 text-sm">
                                        <p className="text-purple-300 text-xs">
                                            A <strong className="text-white">dose</strong> deste produto <strong className="text-white">não aparecerá</strong> na vitrine pública.
                                            O cliente só poderá comprar a garrafa inteira.
                                        </p>
                                        <p className="text-gray-400 text-xs mt-1">
                                            A dose ainda pode ser usada como ingrediente em Produtos Compostos.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-gray-400">Código de Barras</Label>
                            <Input
                                value={formData.barcode}
                                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="EAN-13"
                                data-testid="product-barcode-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Descrição</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="bg-[#121212] border-[#262626] text-white"
                                placeholder="Descrição opcional"
                                data-testid="product-description-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving || uploading}
                            className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400 font-bold"
                            data-testid="save-product-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProductsPage;
